import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/Card';
import { Textarea } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Shuffle, Sparkles, Trophy, RefreshCw } from 'lucide-react';
export function RandomPicker() {
  const [inputText, setInputText] = useState('');
  const [winner, setWinner] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState<{
    id: number;
    left: string;
    delay: string;
    color: string;
  }[]>([]);
  useEffect(() => {
    if (showConfetti) {
      const colors = ['#FFC700', '#FF0000', '#2E3192', '#41BBC7', '#73CD4B', '#ED1E79'];
      const pieces = Array.from({
        length: 50
      }).map((_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        delay: `${Math.random() * 2}s`,
        color: colors[Math.floor(Math.random() * colors.length)]
      }));
      setConfettiPieces(pieces);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showConfetti]);
  const pickRandom = () => {
    const items = inputText.split('\n').map(item => item.trim()).filter(item => item.length > 0);
    if (items.length === 0) return;
    setIsAnimating(true);
    setWinner('');
    setShowConfetti(false);
    // Animate through random items
    let count = 0;
    const maxCount = 25;
    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * items.length);
      setWinner(items[randomIndex]);
      count++;
      if (count >= maxCount) {
        clearInterval(interval);
        setIsAnimating(false);
        setShowConfetti(true);
      }
    }, 100);
  };
  return <div className="container mx-auto px-4 py-8 max-w-4xl relative overflow-hidden min-h-[80vh]" data-id="element-387">
      {showConfetti && <div className="fixed inset-0 pointer-events-none z-50" data-id="element-388">
          {confettiPieces.map(piece => <div key={piece.id} className="absolute top-0 w-3 h-3 rounded-sm animate-confetti" style={{
        left: piece.left,
        backgroundColor: piece.color,
        animationDelay: piece.delay
      }} data-id="element-389" />)}
        </div>}

      <div className="mb-8 text-center" data-id="element-390">
        <h1 className="text-4xl font-bold mb-2 text-gradient" data-id="element-391">
          Random Name Picker
        </h1>
        <p className="text-muted-foreground" data-id="element-392">
          Pick a random winner from your list with style!
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-393">
        <Card className="card-hover border-primary/20" data-id="element-394">
          <CardHeader data-id="element-395">
            <CardTitle className="flex items-center gap-2" data-id="element-396">
              <Shuffle className="w-5 h-5 text-primary" data-id="element-397" />
              Enter Names
            </CardTitle>
            <CardDescription data-id="element-398">One name per line</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4" data-id="element-399">
            <Textarea value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Alice&#10;Bob&#10;Charlie&#10;David&#10;Emma" rows={12} className="font-medium text-lg bg-background/50 focus:bg-background transition-colors" data-id="element-400" />
            <Button onClick={pickRandom} className="w-full text-lg py-6 shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all" disabled={isAnimating || inputText.trim().length === 0} data-id="element-401">
              {isAnimating ? <RefreshCw className="w-5 h-5 mr-2 animate-spin" data-id="element-402" /> : <Sparkles className="w-5 h-5 mr-2" data-id="element-403" />}
              {isAnimating ? 'Picking Winner...' : 'Pick Random Winner'}
            </Button>
          </CardContent>
        </Card>

        <Card className={`card-hover transition-all duration-500 ${winner && !isAnimating ? 'border-yellow-400 shadow-yellow-200/50 shadow-xl scale-105' : ''}`} data-id="element-404">
          <CardHeader data-id="element-405">
            <CardTitle className="flex items-center gap-2" data-id="element-406">
              <Trophy className="w-5 h-5 text-yellow-500" data-id="element-407" />
              The Winner
            </CardTitle>
            <CardDescription data-id="element-408">
              {winner ? '🎉 Congratulations! 🎉' : 'Click "Pick Random Winner" to select'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col items-center justify-center min-h-[300px]" data-id="element-409">
            {winner ? <div className={`text-center transition-all duration-500 ${!isAnimating ? 'animate-winner' : ''}`} data-id="element-410">
                <div className="relative inline-block" data-id="element-411">
                  <Trophy className="w-24 h-24 mx-auto mb-6 text-yellow-500 drop-shadow-lg animate-float" data-id="element-412" />
                  {!isAnimating && <>
                      <Sparkles className="absolute -top-4 -right-4 w-8 h-8 text-yellow-400 animate-pulse" data-id="element-413" />
                      <Sparkles className="absolute -bottom-2 -left-4 w-6 h-6 text-yellow-400 animate-pulse delay-100" data-id="element-414" />
                    </>}
                </div>

                <div className={`p-8 rounded-xl border-4 ${isAnimating ? 'bg-muted border-muted-foreground/20' : 'bg-gradient-to-br from-yellow-100 to-yellow-50 border-yellow-400 dark:from-yellow-900/20 dark:to-yellow-800/20'} shadow-lg transform transition-all`} data-id="element-415">
                  <p className={`text-5xl font-black ${isAnimating ? 'text-muted-foreground' : 'text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-orange-600 dark:from-yellow-400 dark:to-orange-400'}`} data-id="element-416">
                    {winner}
                  </p>
                </div>

                {!isAnimating && <p className="mt-6 text-lg font-medium text-muted-foreground animate-pulse" data-id="element-417">
                    is the lucky winner!
                  </p>}
              </div> : <div className="text-center py-12 text-muted-foreground opacity-50" data-id="element-418">
                <Trophy className="w-16 h-16 mx-auto mb-4" data-id="element-419" />
                <p className="text-lg" data-id="element-420">No winner selected yet</p>
              </div>}
          </CardContent>
        </Card>
      </div>
    </div>;
}